/**
 * Right-pane detail view for the selected execution-ledger record.
 *
 * Two render modes share the same header + scope chips:
 *   - kind === "mission"  → totals chips · goal · nodes[] table · leaves[] table
 *   - kind === "harness"  → metrics chips · error block · missionId backlink
 *
 * Mirrors [ActivityDetailPane.tsx](../activity/ActivityDetailPane.tsx) edge
 * states: no-selection · loading · not-in-view (selection survives a filter
 * that drops the row per handoff §1).
 *
 * `error.code === "awaiting_user_timeout"` renders as
 * "Timed out waiting for input" per handoff §4 — the human-gate-expiry
 * signal (mission gating plan §A.3), not a generic failure.
 */

import { ArrowUpRight, Loader2, Paperclip } from "lucide-react";

import { UploadAttributionPanel } from "@/components/uploads/UploadAttributionPanel";
import type {
  ExecutionLedgerNode,
  ExecutionLedgerRecord,
} from "@/types/execution-ledger";

interface Props {
  selectedId: string | null;
  record: ExecutionLedgerRecord | null;
  leaves: ExecutionLedgerRecord[];
  loading: boolean;
  /** True when the URL selection is set but the API returned no record
   *  (deleted, never existed, or filter-window now excludes it). */
  notInView: boolean;
  error: string | null;
  /** Navigate to another execution by id (URL-bound). */
  onSelect: (id: string) => void;
}

export function ExecutionLedgerDetailPane({
  selectedId,
  record,
  leaves,
  loading,
  notInView,
  error,
  onSelect,
}: Props) {
  if (!selectedId) {
    return (
      <div
        className="flex min-h-0 items-center justify-center rounded-md border border-dashed border-slate-200 p-6 text-sm text-slate-400 dark:border-slate-700"
        data-testid="execution-ledger-detail-empty"
      >
        Select an execution to see its details.
      </div>
    );
  }
  if (loading) {
    return (
      <div className="flex min-h-0 items-center justify-center rounded-md border border-slate-200 p-6 dark:border-slate-700">
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="flex min-h-0 items-start rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-300"
        data-testid="execution-ledger-detail-error"
      >
        {error}
      </div>
    );
  }
  if (notInView || !record) {
    return (
      <div
        className="flex min-h-0 items-center justify-center rounded-md border border-dashed border-slate-200 p-6 text-sm text-slate-400 dark:border-slate-700"
        data-testid="execution-ledger-detail-not-in-view"
      >
        Execution {selectedId.slice(0, 12)}… is not in the current view.
      </div>
    );
  }

  return (
    <div
      className="flex min-h-0 flex-col gap-4 overflow-y-auto rounded-md border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
      data-testid="execution-ledger-detail"
    >
      <Header record={record} onSelect={onSelect} />
      <Summary record={record} />
      <ScopeChips record={record} />

      {record.kind === "mission" ? (
        <MissionBody record={record} leaves={leaves} onSelect={onSelect} />
      ) : record.kind === "upload" ? (
        <UploadBody record={record} />
      ) : (
        <HarnessBody record={record} onSelect={onSelect} />
      )}

      <ErrorBlock error={record.error} />
    </div>
  );
}

// ─── Header ─────────────────────────────────────────────────────────────

function Header({
  record,
  onSelect,
}: {
  record: ExecutionLedgerRecord;
  onSelect: (id: string) => void;
}) {
  return (
    <header className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-sm">
        {record.kind === "upload" && (
          <Paperclip className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
        )}
        <span className={kindPillCls(record.kind)}>{record.kind}</span>
        <span className={statusPillCls(record.status)}>{record.status}</span>
        {record.kind === "harness" && record.missionId && (
          <button
            type="button"
            onClick={() => onSelect(record.missionId!)}
            className="ml-auto inline-flex items-center gap-1 rounded border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100 dark:border-indigo-800 dark:bg-indigo-950 dark:text-indigo-300 dark:hover:bg-indigo-900"
            data-testid="execution-ledger-mission-backlink"
            title="Open the parent mission"
          >
            mission <ArrowUpRight className="h-3 w-3" />
          </button>
        )}
      </div>
      <h2 className="text-base font-semibold">{record.title ?? record.summary}</h2>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
        <span className="font-mono">{record.executionId}</span>
        <span>·</span>
        <span>created {new Date(record.createdAt).toLocaleString()}</span>
        {record.completedAt !== undefined && (
          <>
            <span>·</span>
            <span>
              completed {new Date(record.completedAt).toLocaleString()}
            </span>
          </>
        )}
      </div>
    </header>
  );
}

// ─── Summary line ───────────────────────────────────────────────────────

function Summary({ record }: { record: ExecutionLedgerRecord }) {
  // When `title` is present, `summary` is the secondary essence and
  // worth its own block. When `title` is absent, summary already
  // served as the header — skip the duplicate.
  if (!record.title || !record.summary) return null;
  return (
    <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:bg-slate-800 dark:text-slate-300">
      {record.summary}
    </p>
  );
}

// ─── Scope chips ────────────────────────────────────────────────────────

function ScopeChips({ record }: { record: ExecutionLedgerRecord }) {
  const chips: Array<[string, string | undefined]> = [
    ["org", record.orgId],
    ["user", record.userId],
    ["case", record.caseId],
    ["matter", record.matterId],
    ["client", record.clientId],
  ];
  const present = chips.filter(([, v]) => Boolean(v));
  if (present.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {present.map(([label, value]) => (
        <span
          key={label}
          className="rounded bg-slate-100 px-2 py-0.5 font-mono text-[11px] text-slate-700 dark:bg-slate-800 dark:text-slate-300"
        >
          {label}: {value}
        </span>
      ))}
    </div>
  );
}

// ─── Mission body ──────────────────────────────────────────────────────

function MissionBody({
  record,
  leaves,
  onSelect,
}: {
  record: ExecutionLedgerRecord;
  leaves: ExecutionLedgerRecord[];
  onSelect: (id: string) => void;
}) {
  return (
    <>
      {record.totals && (
        <MetricsRow
          items={[
            ["Cost", fmtCost(record.totals.costUsd)],
            ["Tokens in", fmtTokens(record.totals.tokensIn)],
            ["Tokens out", fmtTokens(record.totals.tokensOut)],
            ["Duration", fmtDuration(record.durationMs)],
          ]}
        />
      )}
      {record.goal && (
        <Section title="Goal">
          <p className="text-sm italic text-slate-700 dark:text-slate-300">
            {record.goal}
          </p>
        </Section>
      )}
      {record.nodes && record.nodes.length > 0 && (
        <Section title={`Nodes (${record.nodes.length})`}>
          <NodesTable nodes={record.nodes} />
        </Section>
      )}
      {leaves.length > 0 && (
        <Section title={`Leaves (${leaves.length})`}>
          <LeavesTable leaves={leaves} onSelect={onSelect} />
        </Section>
      )}
    </>
  );
}

// ─── Harness body ──────────────────────────────────────────────────────

function HarnessBody({
  record,
  onSelect,
}: {
  record: ExecutionLedgerRecord;
  onSelect: (id: string) => void;
}) {
  return (
    <>
      <MetricsRow
        items={[
          ...modelMetrics(record),
          ["Provider", record.provider ?? "—"],
          ["Cost", fmtCost(record.costUsd)],
          ["Tokens in", fmtTokens(record.tokensIn)],
          ["Tokens out", fmtTokens(record.tokensOut)],
          ["Duration", fmtDuration(record.durationMs)],
        ]}
      />
      {(record.harnessId || record.parentTaskId) && (
        <Section title="Identifiers">
          <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
            {record.harnessId && (
              <>
                <dt className="text-slate-500">harness</dt>
                <dd className="font-mono">{record.harnessId}</dd>
              </>
            )}
            {record.parentTaskId && (
              <>
                <dt className="text-slate-500">parent task</dt>
                <dd>
                  <button
                    type="button"
                    onClick={() => onSelect(record.parentTaskId!)}
                    className="font-mono text-indigo-600 hover:underline dark:text-indigo-400"
                  >
                    {record.parentTaskId}
                  </button>
                </dd>
              </>
            )}
          </dl>
        </Section>
      )}
    </>
  );
}

// ─── Upload body (kind: "upload") ──────────────────────────────────────

/**
 * Renders the full 11-key attribution Record (handoff §3.1 layout +
 * canonical schema at ai-docs/upload-attribution-and-delete-permissions.md §2).
 * Handles the four writer classes via `resolveUploaderDisplay`:
 *   - real user (uploadedByEmail) → email + role chip + id (mono, copy-on-click)
 *   - system:lexiun-agent:<harnessName> → "Lexiun Agent (<harness>)" + role "system"
 *   - legacy-unknown → "Unknown (backfilled)" muted gray + role "unknown"
 *   - missing entirely → falls back to "—" + the field-level fallbacks
 *
 * Re-uploaded-since-stamp warning (handoff §3.2) is deferred to a
 * follow-up — no head-only sha lookup is available against the runtime
 * fs proxy today; doing it via `read` would fetch full bytes per drawer
 * open, which is too expensive on the per-row click path.
 */
/** Delegates to the shared UploadAttributionPanel so the ExecutionLedger
 *  detail pane + the RuntimeFsBrowser viewer pane render IDENTICAL
 *  attribution layouts (no drift). The panel handles all 4 writer
 *  classes + click-to-copy + SourceChip + time formatting. */
function UploadBody({ record }: { record: ExecutionLedgerRecord }) {
  return <UploadAttributionPanel record={record} />;
}

// ─── Error block ───────────────────────────────────────────────────────

function ErrorBlock({
  error,
}: {
  error?: ExecutionLedgerRecord["error"];
}) {
  if (!error) return null;
  // Handoff §4: a human-gate timeout is rendered as "timed out waiting
  // for input", not a generic failure. Distinguish at the error.code
  // level — the status is already normalized to "failed" upstream.
  const isAwaitingTimeout = error.code === "awaiting_user_timeout";
  return (
    <Section title="Error" testid="execution-ledger-error-block">
      <div
        className={`rounded-md p-3 text-sm ${
          isAwaitingTimeout
            ? "bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-200"
            : "bg-rose-50 text-rose-900 dark:bg-rose-950 dark:text-rose-200"
        }`}
      >
        <div className="font-mono text-xs uppercase">{error.code}</div>
        <div className="mt-1">
          {isAwaitingTimeout
            ? `Timed out waiting for input — ${error.message}`
            : error.message}
        </div>
      </div>
    </Section>
  );
}

// ─── Generic building blocks ───────────────────────────────────────────

function Section({
  title,
  children,
  testid,
}: {
  title: string;
  children: React.ReactNode;
  testid?: string;
}) {
  return (
    <section className="flex flex-col gap-2" data-testid={testid}>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {title}
      </h3>
      {children}
    </section>
  );
}

/** The two model-identity rows for a harness execution.
 *
 * `record.model` is the harness's DECLARED manifest value — on a v3 manifest a
 * PROFILE ALIAS ("LOW"/"MED"/"MAX"), never the model that answered. Plan §J
 * measured it across 7893 localdev rows: `model` held LOW/MED/MAX/absent and a
 * full-text scan found ZERO real model ids in the collection. `resolvedModels`
 * is the lossless sibling (added 2026-08-26) — the concrete `provider/model`
 * refs that ACTUALLY answered, first-observed order, OMITTED (never `[]`) when
 * nothing was observed.
 *
 * Labelling the alias "Model" was the defect: it reads as an answer to "what
 * ran?" while holding a value that cannot answer it, and a profile-ladder
 * retune leaves every row still reading `LOW`. Both rows now ship, each
 * labelled for what it is; an UNOBSERVED resolution stays "—" rather than
 * borrowing the alias to look answered.
 */
export function modelMetrics(
  record: ExecutionLedgerRecord,
): Array<[string, string]> {
  const resolved =
    record.resolvedModels?.filter((ref) => ref.trim().length > 0) ?? [];
  return [
    ["Model", resolved.length > 0 ? resolved.join(", ") : "—"],
    ["Declared", record.model ?? "—"],
  ];
}

function MetricsRow({ items }: { items: Array<[string, string]> }) {
  return (
    <dl className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
      {items.map(([label, value]) => (
        <div key={label} className="flex items-baseline gap-1">
          <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
          <dd className="font-mono text-slate-900 dark:text-slate-100">
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function NodesTable({ nodes }: { nodes: ExecutionLedgerNode[] }) {
  return (
    <div className="overflow-hidden rounded border border-slate-200 dark:border-slate-700">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          <tr>
            <Th>Node</Th>
            <Th>Harness</Th>
            <Th>Status</Th>
            <Th align="right">Duration</Th>
            <Th align="right">Cost</Th>
            <Th align="right">Tokens (in/out)</Th>
          </tr>
        </thead>
        <tbody>
          {nodes.map((n) => (
            <tr
              key={n.nodeId}
              className="border-t border-slate-100 dark:border-slate-800"
            >
              <Td className="font-mono">{n.nodeId}</Td>
              <Td className="font-mono">{n.harnessId}</Td>
              <Td>
                <span className={statusPillCls(n.status)}>{n.status}</span>
              </Td>
              <Td align="right" className="font-mono">
                {fmtDuration(n.durationMs)}
              </Td>
              <Td align="right" className="font-mono">
                {fmtCost(n.costUsd)}
              </Td>
              <Td align="right" className="font-mono">
                {fmtTokens(n.tokensIn)} / {fmtTokens(n.tokensOut)}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeavesTable({
  leaves,
  onSelect,
}: {
  leaves: ExecutionLedgerRecord[];
  onSelect: (id: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded border border-slate-200 dark:border-slate-700">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          <tr>
            <Th>Leaf</Th>
            <Th>Status</Th>
            <Th align="right">Cost</Th>
            <Th align="right">Duration</Th>
            <Th>{""}</Th>
          </tr>
        </thead>
        <tbody>
          {leaves.map((l) => (
            <tr
              key={l.executionId}
              className="border-t border-slate-100 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800"
            >
              <Td className="max-w-[24ch] truncate">
                {l.title ?? l.harnessId ?? l.summary}
              </Td>
              <Td>
                <span className={statusPillCls(l.status)}>{l.status}</span>
              </Td>
              <Td align="right" className="font-mono">
                {fmtCost(l.costUsd)}
              </Td>
              <Td align="right" className="font-mono">
                {fmtDuration(l.durationMs)}
              </Td>
              <Td align="right">
                <button
                  type="button"
                  onClick={() => onSelect(l.executionId)}
                  className="inline-flex items-center gap-1 text-indigo-600 hover:underline dark:text-indigo-400"
                  data-testid={`execution-ledger-leaf-open-${l.executionId}`}
                >
                  open <ArrowUpRight className="h-3 w-3" />
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={`px-2 py-1.5 font-medium ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  className = "",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <td
      className={`px-2 py-1.5 ${
        align === "right" ? "text-right" : "text-left"
      } ${className}`}
    >
      {children}
    </td>
  );
}

// ─── Formatters ────────────────────────────────────────────────────────

function fmtCost(usd?: number): string {
  if (usd === undefined || usd === null) return "—";
  if (usd === 0) return "$0";
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}

function fmtTokens(n?: number): string {
  if (n === undefined || n === null) return "—";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

function fmtDuration(ms?: number): string {
  if (ms === undefined || ms === null) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3_600_000) return `${(ms / 60_000).toFixed(1)}m`;
  return `${(ms / 3_600_000).toFixed(2)}h`;
}

/** Kind pill — mirrors `kindPillCls` on ExecutionLedger.tsx (kept in
 *  sync by hand; only 3 kinds so the dup is cheap). Mission=violet,
 *  harness=sky, upload=amber. */
function kindPillCls(kind: string): string {
  const base =
    "rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide";
  if (kind === "mission") {
    return `${base} bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200`;
  }
  if (kind === "upload") {
    return `${base} bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200`;
  }
  return `${base} bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200`;
}

// Mirrors the list-row pill on ExecutionLedger.tsx — duplicated here
// rather than exported because the list version is currently a private
// const there and either home would work. Kept in sync by hand for now;
// extract if a third caller ever needs it.
function statusPillCls(status: string): string {
  const base =
    "rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide";
  if (status === "completed") {
    return `${base} bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200`;
  }
  if (status === "canceled") {
    return `${base} bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300`;
  }
  return `${base} bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200`;
}
